import React, { useState, useEffect, useRef, useMemo, forwardRef } from "react";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { registerLocale } from "react-datepicker";
import ru from "date-fns/locale/ru";
import { isValid, parse } from "date-fns";
import "./AddAgreement.css";
import { selectStyles } from "../../../../../utils/selectStyles";
import {
  buildSectionDetailsFromDetailed,
  getProjectEstimate,
  getCommercialProposal,
} from "../../../../../api/projectEstimatesApi";
import {
  getUsersDirectory,
  getAllCounterparties,
} from "../../../../../api/api";
import {
  addAgreement,
  getAgreement,
  getAgreements,
  updateAgreement,
} from "../../../../../api/agreementsApi";
import AddCounterpartyModal from "../../../CounterpartiesDatabase/AddCounterpartyModal/AddCounterpartyModal";
import {
  buildAgreementBlocksPayload,
  buildAgreementBlocksState,
  formatAgreementBlockLabel,
  getAgreementBlockPlanCost,
  getAgreementSectionPlanCost,
  formatDateForApi,
  BASE_PAYMENT_STAGES_COUNT,
  MAX_PAYMENT_STAGES_COUNT,
  normalizePaymentPercentages,
  parseApiDateValue,
  PAYMENT_TEMPLATE_PERCENTAGES,
  resolvePaymentTemplateFromPercentages,
  trimPaymentPercentages,
  resolveUiPerformerTypeFromAgreement,
  matchesPerformerType,
} from "../agreementFormHelpers";

registerLocale("ru", ru);

const CREATE_SUBCONTRACTOR_OPTION = {
  value: "__create_new_subcontractor__",
  label: "+ Создать субподрядчика",
};

const getCounterpartyLabel = (c) =>
  c?.display_name ||
  c?.full_name ||
  c?.full_company_name ||
  c?.short_company_name ||
  c?.short_name ||
  (c?.id != null ? `Контрагент #${c.id}` : "");

const getCounterpartyBin = (c) =>
  String(c?.bin_or_inn || c?.bin_iin || "").replace(/\D/g, "");

const getCounterpartySelectLabel = (c) => {
  const name = getCounterpartyLabel(c);
  const bin = getCounterpartyBin(c);
  return bin ? `${name} / ${bin}` : name;
};

const filterPerformerSelectOption = (option, rawInput) => {
  const query = String(rawInput || "")
    .trim()
    .toLowerCase();
  if (!query) return true;
  const label = String(option?.label || "").toLowerCase();
  if (label.includes(query)) return true;
  const data = option?.data?.data || option?.data;
  const bin = getCounterpartyBin(data);
  const queryDigits = query.replace(/\D/g, "");
  return Boolean(queryDigits && bin.includes(queryDigits));
};

const getEmployeeDisplayName = (user) =>
  user?.full_name?.trim() ||
  [
    user?.surname || user?.lastName,
    user?.name || user?.firstName,
    user?.patronymic || user?.middleName,
  ]
    .filter(Boolean)
    .join(" ")
    .trim() ||
  (user?.id != null ? `Пользователь #${user.id}` : "");

const DEFAULT_GENPROEKT_NAME = "ТОО «AQMOL-project» (Генпроектировщик)";

const formatGenproektDisplayName = (counterparty) => {
  const name = getCounterpartyLabel(counterparty);
  if (!name || name.startsWith("Контрагент #")) return null;
  return `${name} (Генпроектировщик)`;
};

const resolveGenproektFromCommercialProposal = (
  commercialProposal,
  ourCompanies = [],
) => {
  if (!commercialProposal) return null;

  const fromEmbedded = formatGenproektDisplayName(
    commercialProposal.counterparty,
  );
  if (fromEmbedded) return fromEmbedded;

  const counterpartyId = commercialProposal.counterparty_id;
  if (counterpartyId == null) return null;

  const matched = ourCompanies.find(
    (company) => Number(company.id) === Number(counterpartyId),
  );
  return formatGenproektDisplayName(matched);
};

const performerTypes = [
  { value: "contract_employee", label: "Сотрудник на подряд" },
  { value: "individual_gph", label: "Физ лицо на ГПХ" },
  { value: "too_without_vat", label: "ТОО без НДС" },
  { value: "ip", label: "ИП" },
  { value: "too_with_vat", label: "ТОО с НДС" },
];

const PAYMENT_TEMPLATES = [
  { value: "30_50_20", label: "30/50/20 (стандартный)" },
  { value: "50_50", label: "50/50 (аванс / после получения)" },
  { value: "70_30", label: "70/30 (аванс / после получения)" },
  { value: "80_20", label: "80/20 (аванс / после получения)" },
  { value: "100", label: "100% (аванс)" },
  { value: "custom", label: "Свой график" },
];

const PAYMENT_STAGE_TERMS = "Оплата в течение 10 банковских дней";

const BASE_CUSTOM_PAYMENT_STAGE_FIELDS = [
  { key: "advance", label: "Аванс" },
  { key: "second", label: "До госэкспертизы (ОДГЭ)" },
  { key: "third", label: "После госэкспертизы (ОПГЭ)" },
];

const EXTRA_TRANCHE_ORDINALS = [
  "Четвёртый",
  "Пятый",
  "Шестой",
  "Седьмой",
  "Восьмой",
  "Девятый",
  "Десятый",
];

const getExtraStageKey = (index) => `stage_${index + 1}`;

const getExtraStageLabel = (index) => {
  const ordinal = EXTRA_TRANCHE_ORDINALS[index - BASE_PAYMENT_STAGES_COUNT];
  return ordinal ? `${ordinal} транш` : `Транш ${index + 1}`;
};

const getCustomPaymentStageFields = (percentages) =>
  normalizePaymentPercentages(percentages).map(
    (_, index) =>
      BASE_CUSTOM_PAYMENT_STAGE_FIELDS[index] || {
        key: getExtraStageKey(index),
        label: getExtraStageLabel(index),
        removable: true,
      },
  );

/** Описание этапа по умолчанию; для доп. этапов его нет. */
const getDefaultPaymentStageDescription = (index, percentages) => {
  const second = normalizePaymentPercentages(percentages)[1];
  if (index === 0) {
    return "Подписание договора между Генпроектировщиком и Исполнителем";
  }
  if (index === 1) return "Заключение договора с РГП Госэкспертиза";
  if (index === 2) {
    return second === 0
      ? "Принятие Заказчиком проекта и подписание между сторонами (Заказчик и Генпроектировщик) Акта выполненных работ"
      : "Отработка замечаний ДУП, коллизий и BIM, принятие Заказчиком проекта и подписание между сторонами (Заказчик и Генпроектировщик) Акта выполненных работ";
  }
  return null;
};

const getPaymentStageKeyAndLabel = (index, percentages) => {
  const second = normalizePaymentPercentages(percentages)[1];
  if (index === 0) return { key: "advance", label: "Аванс" };
  if (index === 1) return { key: "second", label: "Второй транш" };
  if (index === 2) {
    return {
      key: "third",
      label: second === 0 ? "После получения" : "Третий транш",
    };
  }
  return { key: getExtraStageKey(index), label: getExtraStageLabel(index) };
};

/**
 * Собирает этапы оплаты из [аванс, ОДГЭ, ОПГЭ, ...доп. этапы]; нулевые этапы скрываются.
 * Непустое описание из descriptions[index] заменяет описание по умолчанию.
 */
const buildStagesFromPercentages = (percentages, descriptions = []) => {
  const normalized = normalizePaymentPercentages(percentages);
  return normalized
    .map((percent, index) => {
      if (percent <= 0) return null;
      const customDescription = String(descriptions[index] ?? "").trim();
      return {
        ...getPaymentStageKeyAndLabel(index, normalized),
        percent,
        description:
          customDescription ||
          getDefaultPaymentStageDescription(index, normalized),
        terms: PAYMENT_STAGE_TERMS,
      };
    })
    .filter(Boolean);
};

const normalizePaymentDescriptions = (descriptions, length) =>
  Array.from({ length }, (_, index) =>
    typeof descriptions?.[index] === "string" ? descriptions[index] : "",
  );

const formatPaymentPercentagesLabel = (percentages) => {
  const normalized = normalizePaymentPercentages(percentages);
  const [advance] = normalized;
  const nonZero = normalized.filter((percent) => percent > 0);
  if (nonZero.length === 1 && advance > 0) return `${advance}% (аванс)`;
  return nonZero.join("/");
};

const formatAgreementNumber = (num) => `№${String(num).padStart(7, "0")}`;

const startOfDay = (date) => {
  const d = date instanceof Date ? new Date(date) : new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

/** Плановая дата этапа: из state или дата предыдущего этапа / начала работ */
const getPaymentPlanDateForStage = (
  stages,
  paymentPlanDates,
  executionStartDate,
  stageIndex,
) => {
  const stage = stages[stageIndex];
  const stored = paymentPlanDates[stage.key];
  if (stored) return startOfDay(stored);
  if (stageIndex === 0) return startOfDay(executionStartDate ?? new Date());
  return getPaymentPlanDateForStage(
    stages,
    paymentPlanDates,
    executionStartDate,
    stageIndex - 1,
  );
};

/** Минимально допустимая дата для этапа (не раньше предыдущего транша; аванс — без ограничения) */
const getMinPaymentPlanDateForStage = (
  stages,
  paymentPlanDates,
  executionStartDate,
  stageIndex,
) => {
  if (stageIndex <= 0) return null;
  return getPaymentPlanDateForStage(
    stages,
    paymentPlanDates,
    executionStartDate,
    stageIndex - 1,
  );
};

const parseAgreementNumber = (value) => {
  if (value == null) return 0;
  const parsed = parseInt(String(value).replace(/\D/g, ""), 10);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const createDefaultAgreementDate = () => startOfDay(new Date());

const createDefaultExecutionEndDate = () => {
  const d = new Date();
  d.setDate(d.getDate() + 10);
  return d;
};

const selectNumericInputValue = (event) => {
  const input = event.target;
  requestAnimationFrame(() => input.select());
};

const maskRuDateInputValue = (raw) => {
  const digits = String(raw ?? "")
    .replace(/\D/g, "")
    .slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 4)}.${digits.slice(4)}`;
};

const parseMaskedRuDate = (maskedValue) => {
  if (maskedValue.length !== 10) return null;
  const parsed = parse(maskedValue, "dd.MM.yyyy", new Date());
  return isValid(parsed) ? startOfDay(parsed) : null;
};

const MaskedDateInput = forwardRef(
  (
    { value, onClick, onChange, onBlur, onDateParsed, className, placeholder },
    ref,
  ) => {
    const [localValue, setLocalValue] = useState(value ?? "");
    const isFocusedRef = useRef(false);

    useEffect(() => {
      if (!isFocusedRef.current) {
        setLocalValue(value ?? "");
      }
    }, [value]);

    const commitMaskedValue = (masked, event) => {
      setLocalValue(masked);
      onChange?.({ ...event, target: { ...event.target, value: masked } });
      const parsedDate = parseMaskedRuDate(masked);
      if (parsedDate) {
        onDateParsed?.(parsedDate);
      }
    };

    return (
      <input
        ref={ref}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        value={localValue}
        placeholder={placeholder ?? "дд.мм.гггг"}
        onClick={onClick}
        onFocus={(event) => {
          isFocusedRef.current = true;
          selectNumericInputValue(event);
        }}
        onBlur={(event) => {
          isFocusedRef.current = false;
          const masked = maskRuDateInputValue(event.target.value);
          commitMaskedValue(masked, event);
          onBlur?.(event);
        }}
        onChange={(event) => {
          const masked = maskRuDateInputValue(event.target.value);
          commitMaskedValue(masked, event);
        }}
        className={className}
      />
    );
  },
);
MaskedDateInput.displayName = "MaskedDateInput";

const MaskedRuDatePicker = ({
  selected,
  onChange,
  className,
  minDate,
  maxDate,
  placeholderText = "дд.мм.гггг",
  popperClassName,
}) => {
  const handleParsedDate = (date) => {
    const normalized = startOfDay(date);
    if (minDate && normalized < startOfDay(minDate)) return;
    if (maxDate && normalized > startOfDay(maxDate)) return;
    onChange(normalized);
  };

  return (
    <DatePicker
      selected={selected}
      onChange={(date) => {
        if (date != null && isValid(date)) {
          handleParsedDate(date);
        }
      }}
      customInput={
        <MaskedDateInput
          className={className}
          placeholder={placeholderText}
          onDateParsed={handleParsedDate}
        />
      }
      dateFormat="dd.MM.yyyy"
      locale={ru}
      strictParsing
      minDate={minDate}
      maxDate={maxDate}
      popperClassName={popperClassName}
      placeholderText={placeholderText}
    />
  );
};

const AddAgreement = ({ open, onClose, projectId, agreementId = null }) => {
  const isEditMode = agreementId != null;
  const [currentStep, setCurrentStep] = useState(1);
  const [agreementNumber, setAgreementNumber] = useState("№0000001");
  const [agreementDate, setAgreementDate] = useState(
    createDefaultAgreementDate,
  );
  const [performerType, setPerformerType] = useState("too_with_vat");
  const [selectedPerformer, setSelectedPerformer] = useState(null);
  const [performerOptionsList, setPerformerOptionsList] = useState([]);
  const [loadingPerformers, setLoadingPerformers] = useState(false);

  // Состояние для данных сметы
  const [estimateData, setEstimateData] = useState(null);
  const [sectionsData, setSectionsData] = useState({});
  const [loadingEstimate, setLoadingEstimate] = useState(false);
  const [selectedSections, setSelectedSections] = useState(new Set());
  const [selectedBlocks, setSelectedBlocks] = useState({}); // {sectionId: {blockId: {selected: bool, cost: number}}}
  const [agreementCosts, setAgreementCosts] = useState({}); // {sectionId: {blockId: cost}}

  // Шаг 3: сроки и порядок оплаты
  const [executionStartDate, setExecutionStartDate] = useState(
    () => new Date(),
  );
  const [executionEndDate, setExecutionEndDate] = useState(
    createDefaultExecutionEndDate,
  );
  const [paymentTemplate, setPaymentTemplate] = useState("30_50_20");
  // Ручные проценты [аванс, ОДГЭ, ОПГЭ] для шаблона «Свой график»
  const [customPaymentPercentages, setCustomPaymentPercentages] = useState([
    30, 50, 20,
  ]);
  // Описания этапов для «Своего графика» (индексы совпадают с customPaymentPercentages)
  const [customPaymentDescriptions, setCustomPaymentDescriptions] = useState(
    [],
  );
  // План даты платежей по этапам (ключ — stage.key: advance, second, third)
  const [paymentPlanDates, setPaymentPlanDates] = useState({});
  const [savingAgreement, setSavingAgreement] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveInfo, setSaveInfo] = useState(null);
  const closeTimeoutRef = useRef(null);

  const [showAddSubcontractorModal, setShowAddSubcontractorModal] =
    useState(false);
  const [extraSubcontractors, setExtraSubcontractors] = useState([]);
  const [genproektDisplayName, setGenproektDisplayName] = useState(
    DEFAULT_GENPROEKT_NAME,
  );
  const [editSourceAgreement, setEditSourceAgreement] = useState(null);
  const [loadingEditAgreement, setLoadingEditAgreement] = useState(false);
  const [pendingBlocksApply, setPendingBlocksApply] = useState(false);
  const [pendingPerformerApply, setPendingPerformerApply] = useState(false);

  const getNextAgreementNumber = async () => {
    const data = await getAgreements({
      ordering: "-number",
      page: 1,
      page_size: 1,
    });
    const items = data?.results || data?.items || [];
    const maxNumber = items.reduce((max, item) => {
      const current = parseAgreementNumber(item?.number);
      return current > max ? current : max;
    }, 0);
    return maxNumber + 1;
  };

  useEffect(() => {
    if (!open) return;

    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }

    setCurrentStep(1);
    setAgreementDate(createDefaultAgreementDate());
    setPerformerType("too_with_vat");
    setSelectedPerformer(null);
    setPerformerOptionsList([]);
    setLoadingPerformers(false);
    setEstimateData(null);
    setSectionsData({});
    setLoadingEstimate(false);
    setSelectedSections(new Set());
    setSelectedBlocks({});
    setAgreementCosts({});
    setExecutionStartDate(new Date());
    setExecutionEndDate(createDefaultExecutionEndDate());
    setPaymentTemplate("30_50_20");
    setCustomPaymentPercentages([30, 50, 20]);
    setCustomPaymentDescriptions([]);
    setPaymentPlanDates({});
    setSavingAgreement(false);
    setSaveError(null);
    setSaveInfo(null);
    setShowAddSubcontractorModal(false);
    setExtraSubcontractors([]);
    setGenproektDisplayName(DEFAULT_GENPROEKT_NAME);
    setEditSourceAgreement(null);
    setLoadingEditAgreement(false);
    setPendingBlocksApply(false);
    setPendingPerformerApply(false);

    if (isEditMode) {
      const loadAgreementForEdit = async () => {
        setLoadingEditAgreement(true);
        try {
          const agreement = await getAgreement(agreementId);
          setEditSourceAgreement(agreement);
          setAgreementNumber(formatAgreementNumber(agreement?.number ?? 0));
          setAgreementDate(
            parseApiDateValue(agreement?.date_from) ||
              createDefaultAgreementDate(),
          );
          const uiPerformerType = await resolveUiPerformerTypeFromAgreement(
            agreement,
            getAllCounterparties,
          );
          setPerformerType(uiPerformerType);
          setExecutionStartDate(
            parseApiDateValue(agreement?.date_from) || new Date(),
          );
          setExecutionEndDate(
            parseApiDateValue(agreement?.date_to) ||
              createDefaultExecutionEndDate(),
          );
          const percentages = agreement?.payment_schedule?.percentages;
          const resolvedTemplate =
            resolvePaymentTemplateFromPercentages(percentages);
          setPaymentTemplate(resolvedTemplate);
          if (resolvedTemplate === "custom") {
            const normalizedPercentages =
              normalizePaymentPercentages(percentages);
            setCustomPaymentPercentages(normalizedPercentages);
            setCustomPaymentDescriptions(
              normalizePaymentDescriptions(
                agreement?.payment_schedule?.labels,
                normalizedPercentages.length,
              ),
            );
          } else if (PAYMENT_TEMPLATE_PERCENTAGES[resolvedTemplate]) {
            setCustomPaymentPercentages([
              ...PAYMENT_TEMPLATE_PERCENTAGES[resolvedTemplate],
            ]);
          }
          setPendingBlocksApply(true);
          setPendingPerformerApply(true);
        } catch (err) {
          console.error("Не удалось загрузить соглашение:", err);
          setSaveError(err.message || "Не удалось загрузить соглашение");
        } finally {
          setLoadingEditAgreement(false);
        }
      };

      loadAgreementForEdit();
      return;
    }

    const loadNextAgreementNumber = async () => {
      try {
        const nextNumber = await getNextAgreementNumber();
        setAgreementNumber(formatAgreementNumber(nextNumber));
      } catch (err) {
        console.error("Не удалось определить следующий номер соглашения:", err);
        setAgreementNumber(formatAgreementNumber(1));
      }
    };

    loadNextAgreementNumber();
  }, [open, projectId, agreementId, isEditMode]);

  useEffect(() => {
    if (!open || !projectId) return undefined;

    let cancelled = false;

    const loadGenproektFromCommercialProposal = async () => {
      try {
        const commercialProposal = await getCommercialProposal(
          Number(projectId),
        );
        if (cancelled) return;

        let ourCompanies = [];
        if (
          commercialProposal?.counterparty_id != null &&
          !commercialProposal?.counterparty
        ) {
          const list = await getAllCounterparties({
            counterparty_side: "our_company",
          });
          ourCompanies = Array.isArray(list) ? list : [];
        }

        const resolved = resolveGenproektFromCommercialProposal(
          commercialProposal,
          ourCompanies,
        );
        if (!cancelled && resolved) {
          setGenproektDisplayName(resolved);
        }
      } catch {
        if (!cancelled) {
          setGenproektDisplayName(DEFAULT_GENPROEKT_NAME);
        }
      }
    };

    loadGenproektFromCommercialProposal();

    return () => {
      cancelled = true;
    };
  }, [open, projectId]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const handleStepClick = (step) => {
    // Можно переходить к любому уже пройденному шагу или следующему
    if (step <= currentStep + 1) {
      setCurrentStep(step);
    }
  };

  // Загрузка опций исполнителя: «Сотрудник на подряд» — directory (все пользователи), остальные — контрагенты с фильтрами
  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    const loadPerformerOptions = async () => {
      setLoadingPerformers(true);
      setPerformerOptionsList([]);
      try {
        if (performerType === "contract_employee") {
          const users = await getUsersDirectory();
          if (cancelled) return;
          const options = (Array.isArray(users) ? users : [])
            .map((user) => {
              const name = getEmployeeDisplayName(user);
              const label = user.is_fired ? `${name} (уволен)` : name;
              return {
                value: user.id,
                label,
                data: { _source: "user", ...user },
              };
            })
            .sort((a, b) =>
              a.label.localeCompare(b.label, "ru", { sensitivity: "base" }),
            );
          setPerformerOptionsList(options);
        } else {
          const list = await getAllCounterparties({
            counterparty_side: "counterparty",
          });
          if (cancelled) return;
          const arr = Array.isArray(list) ? list : [];
          const options = arr
            .filter((item) => matchesPerformerType(item, performerType))
            .map((item) => ({
              value: item.id,
              label: getCounterpartySelectLabel(item),
              data: { _source: "counterparty", ...item },
            }));
          setPerformerOptionsList(options);
        }
      } catch (err) {
        if (cancelled) return;
        console.error("Ошибка загрузки списка исполнителей:", err);
        setPerformerOptionsList([]);
      } finally {
        if (!cancelled) setLoadingPerformers(false);
      }
    };

    loadPerformerOptions();
    return () => {
      cancelled = true;
    };
  }, [open, performerType]);

  // Загрузка данных сметы при переходе на второй шаг или при редактировании
  useEffect(() => {
    if (!projectId || estimateData) return;
    if (currentStep === 2 || isEditMode) {
      loadEstimateData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, projectId, isEditMode]);

  useEffect(() => {
    if (
      !pendingBlocksApply ||
      !editSourceAgreement ||
      Object.keys(sectionsData).length === 0
    ) {
      return;
    }

    const { selectedSections, selectedBlocks, agreementCosts } =
      buildAgreementBlocksState(editSourceAgreement, sectionsData);
    setSelectedSections(selectedSections);
    setSelectedBlocks(selectedBlocks);
    setAgreementCosts(agreementCosts);
    setPendingBlocksApply(false);
  }, [pendingBlocksApply, editSourceAgreement, sectionsData]);

  useEffect(() => {
    if (
      !open ||
      !pendingPerformerApply ||
      !editSourceAgreement ||
      loadingPerformers ||
      selectedPerformer
    ) {
      return;
    }

    if (performerType === "contract_employee") {
      const userId = editSourceAgreement.performer_ids?.[0];
      const option = performerOptionsList.find(
        (item) => Number(item.value) === Number(userId),
      );
      if (option) {
        setSelectedPerformer(option.data);
      }
    } else {
      const counterpartyId = editSourceAgreement.counterparty_id;
      const option = performerOptionsList.find(
        (item) => Number(item.value) === Number(counterpartyId),
      );
      if (option) {
        setSelectedPerformer(option.data);
      } else if (
        counterpartyId != null &&
        editSourceAgreement.counterparty_summary
      ) {
        const summary = editSourceAgreement.counterparty_summary;
        const fallbackPerformer = {
          _source: "counterparty",
          id: counterpartyId,
          display_name: summary.display_name,
          full_name: summary.full_name,
          full_company_name: summary.full_company_name,
          short_company_name: summary.short_company_name,
        };
        setExtraSubcontractors([fallbackPerformer]);
        setSelectedPerformer(fallbackPerformer);
      }
    }

    setPendingPerformerApply(false);
  }, [
    open,
    pendingPerformerApply,
    editSourceAgreement,
    performerType,
    performerOptionsList,
    loadingPerformers,
    selectedPerformer,
  ]);

  const selectedPaymentPercentages = useMemo(() => {
    if (paymentTemplate === "custom") {
      return normalizePaymentPercentages(customPaymentPercentages);
    }
    return (
      PAYMENT_TEMPLATE_PERCENTAGES[paymentTemplate] ||
      PAYMENT_TEMPLATE_PERCENTAGES["30_50_20"]
    );
  }, [paymentTemplate, customPaymentPercentages]);

  const selectedTemplateStages = useMemo(
    () =>
      buildStagesFromPercentages(
        selectedPaymentPercentages,
        paymentTemplate === "custom" ? customPaymentDescriptions : [],
      ),
    [selectedPaymentPercentages, paymentTemplate, customPaymentDescriptions],
  );

  const customPaymentPercentagesSum = useMemo(
    () =>
      customPaymentPercentages.reduce(
        (sum, value) => sum + (Number(value) || 0),
        0,
      ),
    [customPaymentPercentages],
  );

  const isPaymentScheduleValid =
    paymentTemplate !== "custom" || customPaymentPercentagesSum === 100;

  useEffect(() => {
    if (selectedTemplateStages.length === 0) return;
    setPaymentPlanDates((prev) => {
      let next = prev;
      let changed = false;
      for (let i = 1; i < selectedTemplateStages.length; i++) {
        const key = selectedTemplateStages[i].key;
        const stored = next[key];
        if (!stored) continue;
        const minDate = getMinPaymentPlanDateForStage(
          selectedTemplateStages,
          next,
          executionStartDate,
          i,
        );
        if (minDate && startOfDay(stored) < minDate) {
          if (!changed) next = { ...prev };
          next[key] = minDate;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [executionStartDate, selectedTemplateStages]);

  const loadEstimateData = async () => {
    if (!projectId) return;
    setLoadingEstimate(true);
    try {
      const estimate = await getProjectEstimate(projectId);
      setEstimateData(estimate);

      const contractSections = estimate?.contract_sections?.sections || [];
      const constructionSections = [
        ...(estimate.construction_design || []),
        ...(estimate.construction_design_project || []),
      ];
      const allSections = [...contractSections, ...constructionSections];

      // Детали секций из одного ответа (construction_design_detailed), порядок блоков как в смете
      const detailedBySectionId = buildSectionDetailsFromDetailed(
        estimate.construction_design_detailed,
      );

      const sectionsDetails = {};
      for (const section of allSections) {
        const sid = section.section?.id;
        if (sid == null) continue;
        sectionsDetails[sid] = {
          ...section,
          block_expenditures:
            detailedBySectionId[sid]?.block_expenditures || [],
        };
      }
      setSectionsData(sectionsDetails);
    } catch (err) {
      console.error("Ошибка загрузки данных сметы:", err);
    } finally {
      setLoadingEstimate(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (
      e.target.closest(".add-counterparty-modal") ||
      e.target.closest(".add-counterparty-modal-backdrop")
    ) {
      return;
    }
    onClose();
  };

  if (!open) return null;

  if (loadingEditAgreement) {
    return (
      <div className="add-agreement-overlay" onClick={handleOverlayClick}>
        <div
          className="add-agreement-container"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="add-agreement-header">
            <h2 className="add-agreement-title">Редактирование соглашения</h2>
            <button className="add-agreement-close" onClick={onClose}>
              ×
            </button>
          </div>
          <p className="add-agreement-loading">Загрузка соглашения…</p>
        </div>
      </div>
    );
  }

  const handlePerformerSelect = (option) => {
    setSelectedPerformer(option?.data || null);
  };

  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const performerData = selectedPerformer;
  const isEmployeePerformer = performerData?._source === "user";
  const isCounterpartyPerformer = performerData?._source === "counterparty";

  const formatCounterpartyAddress = (c) => {
    if (!c) return "—";
    const parts = [
      c.country,
      c.postal_code,
      c.city,
      c.street,
      c.building,
    ].filter(Boolean);
    return parts.length ? parts.join(", ") : "—";
  };

  // Общая сумма соглашения со шага «Стоимость по блокам»
  const getTotalAgreementCost = () => {
    return Array.from(selectedSections).reduce((sum, sectionId) => {
      const sectionCosts = agreementCosts[sectionId] || {};
      return (
        sum +
        Object.values(sectionCosts).reduce((s, cost) => s + (cost || 0), 0)
      );
    }, 0);
  };

  const totalAgreementCost = getTotalAgreementCost();

  // Наименование субподрядчика (выбран на шаге 1)
  const getSubcontractorName = () => {
    if (!selectedPerformer) return "—";
    if (selectedPerformer._source === "user") {
      return getEmployeeDisplayName(selectedPerformer) || "—";
    }
    return (
      selectedPerformer.full_name ||
      selectedPerformer.full_company_name ||
      selectedPerformer.short_company_name ||
      selectedPerformer.short_name ||
      "—"
    );
  };

  // Предмет соглашения: разделы + объект (из сметы/проекта)
  const getAgreementSubject = () => {
    const sectionNames = Array.from(selectedSections)
      .map((sectionId) => sectionsData[sectionId]?.section?.name)
      .filter(Boolean);
    const namesStr = sectionNames.length
      ? sectionNames.join(" и ")
      : "выбранным разделам";
    const objectName =
      estimateData?.project?.name || estimateData?.name || "объекта";
    return `Выполнение проектных работ по разделам ${namesStr} для ${objectName}`;
  };

  // Рабочие дни между датами (без учёта праздников)
  const countWorkingDays = (start, end) => {
    if (!start || !end || end < start) return 0;
    let count = 0;
    const d = new Date(start);
    d.setHours(0, 0, 0, 0);
    const endD = new Date(end);
    endD.setHours(0, 0, 0, 0);
    while (d <= endD) {
      const day = d.getDay();
      if (day !== 0 && day !== 6) count += 1;
      d.setDate(d.getDate() + 1);
    }
    return count;
  };

  const formatDateRange = (start, end) => {
    if (!start || !end) return "—";
    const fmt = (d) =>
      d.toLocaleDateString("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    const days = countWorkingDays(start, end);
    return days > 0
      ? `${fmt(start)}–${fmt(end)} (${days} рабочих дней)`
      : `${fmt(start)}–${fmt(end)}`;
  };

  const handlePaymentPlanDateChange = (stageKey, stageIndex, date) => {
    if (!date || selectedTemplateStages.length === 0) return;
    const minDate = getMinPaymentPlanDateForStage(
      selectedTemplateStages,
      paymentPlanDates,
      executionStartDate,
      stageIndex,
    );
    let effectiveDate = startOfDay(date);
    if (minDate && effectiveDate < minDate) effectiveDate = minDate;

    setPaymentPlanDates((prev) => {
      const next = { ...prev, [stageKey]: effectiveDate };
      for (let i = stageIndex + 1; i < selectedTemplateStages.length; i++) {
        const laterKey = selectedTemplateStages[i].key;
        const laterStored = next[laterKey];
        if (!laterStored) continue;
        const laterMin = getMinPaymentPlanDateForStage(
          selectedTemplateStages,
          next,
          executionStartDate,
          i,
        );
        if (laterMin && startOfDay(laterStored) < laterMin) {
          next[laterKey] = laterMin;
        }
      }
      return next;
    });
  };

  const handlePaymentTemplateChange = (opt) => {
    const value = opt?.value || "30_50_20";
    if (value === "custom") {
      setCustomPaymentPercentages(
        paymentTemplate === "custom"
          ? normalizePaymentPercentages(customPaymentPercentages)
          : [
              ...(PAYMENT_TEMPLATE_PERCENTAGES[paymentTemplate] || [
                30, 50, 20,
              ]),
            ],
      );
    } else if (PAYMENT_TEMPLATE_PERCENTAGES[value]) {
      setCustomPaymentPercentages([...PAYMENT_TEMPLATE_PERCENTAGES[value]]);
    }
    setPaymentTemplate(value);
  };

  const handleCustomPercentChange = (index, rawValue) => {
    const digits = String(rawValue ?? "").replace(/[^\d]/g, "");
    const nextValue =
      digits === "" ? 0 : Math.min(100, Math.max(0, Number(digits)));
    setCustomPaymentPercentages((prev) => {
      const next = normalizePaymentPercentages(prev);
      next[index] = nextValue;
      return next;
    });
  };

  const handleCustomDescriptionChange = (index, value) => {
    setCustomPaymentDescriptions((prev) => {
      const next = normalizePaymentDescriptions(
        prev,
        Math.max(prev.length, index + 1),
      );
      next[index] = value;
      return next;
    });
  };

  const handleAddCustomPaymentStage = () => {
    const current = normalizePaymentPercentages(customPaymentPercentages);
    if (current.length >= MAX_PAYMENT_STAGES_COUNT) return;
    setCustomPaymentPercentages([...current, 0]);
    setCustomPaymentDescriptions((prev) => [
      ...normalizePaymentDescriptions(prev, current.length),
      "",
    ]);
  };

  const handleRemoveCustomPaymentStage = (index) => {
    if (index < BASE_PAYMENT_STAGES_COUNT) return;
    const current = normalizePaymentPercentages(customPaymentPercentages);
    setCustomPaymentPercentages(current.filter((_, i) => i !== index));
    setCustomPaymentDescriptions((prev) =>
      normalizePaymentDescriptions(prev, current.length).filter(
        (_, i) => i !== index,
      ),
    );
    setPaymentPlanDates((prev) => {
      const next = { ...prev };
      for (let i = index; i < MAX_PAYMENT_STAGES_COUNT; i++) {
        delete next[getExtraStageKey(i)];
      }
      return next;
    });
  };

  // Собираем ID блоков по выбранным разделам и блокам (из block_expenditures)
  const getSectionIdsForPayload = () => {
    return Array.from(selectedSections)
      .map((sectionId) => Number(sectionId))
      .filter((sectionId) => Number.isFinite(sectionId) && sectionId > 0);
  };

  const getAgreementSectionGroups = () => {
    const contractSections = estimateData?.contract_sections?.sections || [];
    const constructionSections = [
      ...(estimateData?.construction_design || []),
      ...(estimateData?.construction_design_project || []),
    ];

    return {
      contractSections,
      constructionSections,
    };
  };

  const findSectionById = (sectionId) => {
    const numericSectionId = Number(sectionId);
    const { contractSections, constructionSections } =
      getAgreementSectionGroups();
    return [...contractSections, ...constructionSections].find(
      (s) => Number(s?.section?.id) === numericSectionId,
    );
  };

  const getApiPerformerType = (type) => {
    if (type === "contract_employee") return "contract_employee";
    if (type === "individual_gph") return "gph_physical";
    if (
      type === "ip" ||
      type === "too_without_vat" ||
      type === "too_with_vat"
    ) {
      return "counterparty";
    }
    return type;
  };

  const isAgreementNumberConflictError = (err) =>
    String(err?.message || "")
      .toLowerCase()
      .includes("agreement with number") &&
    String(err?.message || "")
      .toLowerCase()
      .includes("already exists");

  const buildPaymentSchedulePayload = () => {
    const percentages = trimPaymentPercentages(selectedPaymentPercentages);
    if (paymentTemplate !== "custom") return { percentages };

    const labels = normalizePaymentDescriptions(
      customPaymentDescriptions,
      percentages.length,
    ).map((label) => label.trim());
    if (labels.every((label) => !label)) {
      return { percentages };
    }
    return { percentages, labels };
  };

  const buildSavePayload = async () => {
    const {
      block_ids: blockIds,
      blocks,
      section_prices: sectionPrices,
    } = buildAgreementBlocksPayload(
      selectedSections,
      sectionsData,
      agreementCosts,
    );
    const sectionIds = getSectionIdsForPayload();
    const currentNumber = parseAgreementNumber(agreementNumber);

    const payload = {
      number: isEditMode
        ? currentNumber
        : currentNumber > 0
          ? currentNumber
          : await getNextAgreementNumber(),
      status: isEditMode ? (editSourceAgreement?.status ?? "draft") : "draft",
      date_from: formatDateForApi(executionStartDate),
      date_to: formatDateForApi(executionEndDate),
      performer_type: getApiPerformerType(performerType),
      project_id: Number(projectId) || 0,
      price: Math.round(totalAgreementCost),
      smeta_version:
        estimateData?.version ?? editSourceAgreement?.smeta_version ?? 0,
      block_ids: blockIds,
      section_ids: sectionIds,
      blocks,
      section_prices: sectionPrices,
      payment_schedule: buildPaymentSchedulePayload(),
    };

    if (isEditMode && editSourceAgreement?.company_id != null) {
      payload.company_id = Number(editSourceAgreement.company_id);
    }

    if (selectedPerformer?._source === "counterparty") {
      payload.counterparty_id = Number(selectedPerformer.id) || 0;
    }
    if (selectedPerformer?._source === "user") {
      payload.performer_ids = [Number(selectedPerformer.id) || 0];
    }

    return payload;
  };

  const handleSave = async () => {
    setSaveError(null);
    setSaveInfo(null);

    if (!isPaymentScheduleValid) {
      setSaveError(
        `Сумма процентов должна быть 100% (сейчас ${customPaymentPercentagesSum}%)`,
      );
      return;
    }

    setSavingAgreement(true);
    try {
      const payload = await buildSavePayload();

      if (isEditMode) {
        await updateAgreement(agreementId, payload);
        onClose();
        return;
      }

      let wasNumberAutoAdjusted = false;
      try {
        await addAgreement(payload);
      } catch (err) {
        if (!isAgreementNumberConflictError(err)) {
          throw err;
        }
        const nextNumber = await getNextAgreementNumber();
        payload.number = nextNumber;
        setAgreementNumber(formatAgreementNumber(nextNumber));
        wasNumberAutoAdjusted = true;
        await addAgreement(payload);
      }
      if (wasNumberAutoAdjusted) {
        setSaveInfo(
          `Номер был занят и автоматически изменен на ${formatAgreementNumber(
            payload.number,
          )}. Соглашение успешно создано.`,
        );
        closeTimeoutRef.current = setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        onClose();
      }
    } catch (err) {
      setSaveError(
        err.message ||
          (isEditMode
            ? "Не удалось обновить соглашение"
            : "Не удалось сохранить соглашение"),
      );
    } finally {
      setSavingAgreement(false);
    }
  };

  return (
    <div className="add-agreement-overlay" onClick={handleOverlayClick}>
      <div
        className="add-agreement-container"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="add-agreement-header">
          <h2 className="add-agreement-title">
            {isEditMode ? "Редактирование соглашения" : "Создание соглашения"}
          </h2>
          <button className="add-agreement-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="add-agreement-progress">
          <div
            className={`progress-step ${currentStep >= 1 ? "active" : ""} ${
              currentStep > 1 ? "completed" : ""
            } ${currentStep >= 1 ? "clickable" : ""}`}
            onClick={() => handleStepClick(1)}
          >
            <div className="progress-step-number">1</div>
            <div className="progress-step-label">Сведения о субподрядчике</div>
          </div>
          <div
            className={`progress-step ${currentStep >= 2 ? "active" : ""} ${
              currentStep > 2 ? "completed" : ""
            } ${currentStep >= 2 ? "clickable" : ""}`}
            onClick={() => handleStepClick(2)}
          >
            <div className="progress-step-number">2</div>
            <div className="progress-step-label">Состав работ и стоимость</div>
          </div>
          <div
            className={`progress-step ${currentStep >= 3 ? "active" : ""} ${
              currentStep >= 3 ? "clickable" : ""
            }`}
            onClick={() => handleStepClick(3)}
          >
            <div className="progress-step-number">3</div>
            <div className="progress-step-label">Сроки и порядок оплаты</div>
          </div>
        </div>

        {currentStep === 1 && (
          <div className="add-agreement-content">
            <div className="agreement-info-section">
              <h3 className="section-label">Соглашение</h3>
              <div className="agreement-info-fields">
                <div className="agreement-field">
                  <input
                    type="text"
                    value={agreementNumber}
                    onChange={(e) => setAgreementNumber(e.target.value)}
                    className="agreement-input"
                  />
                </div>
                <div className="agreement-field">
                  <MaskedRuDatePicker
                    selected={agreementDate}
                    onChange={setAgreementDate}
                    className="agreement-input agreement-datepicker"
                    popperClassName="add-agreement-datepicker-popper"
                  />
                </div>
              </div>
            </div>

            <div className="subcontractor-info-section">
              <h3 className="section-title">Сведения о субподрядчике</h3>

              <div className="form-group">
                <div className="form-group-header">
                  <label className="form-label">Выбор субподрядчика</label>
                </div>
                <p className="form-description">
                  Выберите субподрядчика из справочника контрагентов
                </p>
              </div>

              <div className="form-group">
                <label className="form-label">Тип исполнителя</label>
                <div className="radio-group">
                  {performerTypes.map((type) => (
                    <label key={type.value} className="radio-label">
                      <input
                        type="radio"
                        name="performerType"
                        value={type.value}
                        checked={performerType === type.value}
                        onChange={(e) => {
                          setPerformerType(e.target.value);
                          setSelectedPerformer(null);
                        }}
                        className="radio-input"
                      />
                      <span className="radio-custom"></span>
                      <span className="radio-text">{type.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">
                  Выберите исполнителя<span className="required">*</span>
                </label>
                {(() => {
                  const performerSelectOptions =
                    performerType === "contract_employee"
                      ? performerOptionsList
                      : [
                          CREATE_SUBCONTRACTOR_OPTION,
                          ...extraSubcontractors
                            .filter((c) =>
                              matchesPerformerType(c, performerType),
                            )
                            .map((c) => ({
                              value: c.id,
                              label: getCounterpartySelectLabel(c),
                              data: { _source: "counterparty", ...c },
                            })),
                          ...performerOptionsList,
                        ];
                  const selectedOption = selectedPerformer
                    ? performerSelectOptions.find(
                        (opt) => opt.value === selectedPerformer.id,
                      ) ||
                      (selectedPerformer?.id != null
                        ? {
                            value: selectedPerformer.id,
                            label:
                              selectedPerformer._source === "user"
                                ? getEmployeeDisplayName(selectedPerformer)
                                : getCounterpartySelectLabel(selectedPerformer),
                            data: selectedPerformer,
                          }
                        : null)
                    : null;
                  return (
                    <Select
                      styles={selectStyles}
                      options={performerSelectOptions}
                      value={selectedOption}
                      onChange={(option) => {
                        if (
                          option?.value === CREATE_SUBCONTRACTOR_OPTION.value
                        ) {
                          setShowAddSubcontractorModal(true);
                          return;
                        }
                        handlePerformerSelect(option);
                      }}
                      isLoading={loadingPerformers}
                      placeholder="Выберите исполнителя..."
                      isSearchable
                      filterOption={filterPerformerSelectOption}
                      noOptionsMessage={() =>
                        loadingPerformers
                          ? "Загрузка..."
                          : "Нет доступных исполнителей"
                      }
                      formatOptionLabel={(option) =>
                        option?.value === CREATE_SUBCONTRACTOR_OPTION.value ? (
                          <span style={{ color: "#2a5e38", fontWeight: 500 }}>
                            {option.label}
                          </span>
                        ) : (
                          option?.label
                        )
                      }
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  );
                })()}
              </div>

              {performerData && (
                <div className="requisites-section">
                  <h4 className="requisites-title">Реквизиты:</h4>
                  <div className="requisites-grid">
                    {isEmployeePerformer ? (
                      <>
                        <div className="requisite-item">
                          <span className="requisite-label">ФИО:</span>
                          <span className="requisite-value">
                            {getEmployeeDisplayName(performerData) || "—"}
                          </span>
                        </div>
                        {(performerData.email || performerData.phone) && (
                          <>
                            {performerData.email && (
                              <div className="requisite-item">
                                <span className="requisite-label">Email:</span>
                                <a
                                  href={`mailto:${performerData.email}`}
                                  className="requisite-value requisite-link"
                                >
                                  {performerData.email}
                                </a>
                              </div>
                            )}
                            {performerData.phone && (
                              <div className="requisite-item">
                                <span className="requisite-label">
                                  Телефон:
                                </span>
                                <span className="requisite-value">
                                  {performerData.phone}
                                </span>
                              </div>
                            )}
                          </>
                        )}
                      </>
                    ) : isCounterpartyPerformer ? (
                      <>
                        <div className="requisite-item">
                          <span className="requisite-label">БИН/ИИН:</span>
                          <span className="requisite-value">
                            {performerData.bin_or_inn || "—"}
                          </span>
                        </div>
                        <div className="requisite-item">
                          <span className="requisite-label">Адрес:</span>
                          <span className="requisite-value">
                            {formatCounterpartyAddress(performerData)}
                          </span>
                        </div>
                        <div className="requisite-item">
                          <span className="requisite-label">
                            Контактное лицо:
                          </span>
                          <span className="requisite-value">
                            {performerData.type === "legal_entity"
                              ? performerData.full_name_first_supervisor
                                ? performerData.job_title
                                  ? `${performerData.full_name_first_supervisor} (${performerData.job_title})`
                                  : `${performerData.full_name_first_supervisor} (директор)`
                                : "—"
                              : performerData.full_name || "—"}
                          </span>
                        </div>
                        <div className="requisite-item">
                          <span className="requisite-label">Телефон:</span>
                          <span className="requisite-value">
                            {performerData.phone || "—"}
                          </span>
                        </div>
                        <div className="requisite-item">
                          <span className="requisite-label">Email:</span>
                          <span className="requisite-value">
                            {performerData.email ? (
                              <a
                                href={`mailto:${performerData.email}`}
                                className="requisite-value requisite-link"
                              >
                                {performerData.email}
                              </a>
                            ) : (
                              "—"
                            )}
                          </span>
                        </div>
                      </>
                    ) : null}
                  </div>
                </div>
              )}
            </div>

            <div className="add-agreement-actions">
              <button
                type="button"
                className="btn-next"
                onClick={handleNext}
                disabled={!selectedPerformer}
              >
                Далее
              </button>
            </div>
          </div>
        )}

        {currentStep === 2 && (
          <div className="add-agreement-content">
            {loadingEstimate ? (
              <div className="loading-message">Загрузка данных сметы...</div>
            ) : (
              <>
                <div className="agreement-info-display">
                  <span className="agreement-info-text">
                    Соглашение {agreementNumber} от{" "}
                    {agreementDate
                      ? agreementDate.toLocaleDateString("ru-RU", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                        })
                      : ""}
                  </span>
                </div>

                <h3 className="section-title">Состав работ и стоимость</h3>

                {/* Выбор разделов */}
                <div className="sections-selection-section">
                  <h4 className="subsection-title">
                    Выбор разделов для субподряда
                  </h4>
                  <p className="subsection-description">
                    Отметьте разделы, которые будут выполняться субподрядчиком
                  </p>
                  {estimateData &&
                    (() => {
                      const { contractSections, constructionSections } =
                        getAgreementSectionGroups();
                      const renderSections = (sections, sectionTypeLabel) => (
                        <div className="sections-grid">
                          {sections.map((section) => {
                            const sectionId = section.section.id;
                            const isSelected = selectedSections.has(sectionId);
                            const sectionBlocksData =
                              sectionsData[sectionId]?.block_expenditures || [];
                            const totalCost = getAgreementSectionPlanCost(
                              section,
                              sectionBlocksData,
                            );

                            return (
                              <div
                                key={sectionId}
                                className={`section-card ${
                                  isSelected ? "selected" : ""
                                }`}
                                onClick={() => {
                                  const newSelected = new Set(selectedSections);
                                  if (isSelected) {
                                    newSelected.delete(sectionId);
                                    // Удаляем выбранные блоки для этого раздела
                                    const newSelectedBlocks = {
                                      ...selectedBlocks,
                                    };
                                    delete newSelectedBlocks[sectionId];
                                    setSelectedBlocks(newSelectedBlocks);
                                    const newAgreementCosts = {
                                      ...agreementCosts,
                                    };
                                    delete newAgreementCosts[sectionId];
                                    setAgreementCosts(newAgreementCosts);
                                  } else {
                                    newSelected.add(sectionId);
                                    const hasDetailedBlocks =
                                      (
                                        sectionsData[sectionId]
                                          ?.block_expenditures || []
                                      ).length > 0;
                                    // Для разделов договора (без block_expenditures) сумма задаётся вручную
                                    if (!hasDetailedBlocks) {
                                      const initialCost =
                                        getAgreementSectionPlanCost(
                                          section,
                                          sectionsData[sectionId]
                                            ?.block_expenditures || [],
                                        );
                                      setAgreementCosts((prev) => ({
                                        ...prev,
                                        [sectionId]: {
                                          ...(prev[sectionId] || {}),
                                          section_total: initialCost,
                                        },
                                      }));
                                    }
                                  }
                                  setSelectedSections(newSelected);
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => {}}
                                  className="section-checkbox"
                                />
                                <div className="section-card-content">
                                  <div className="section-code">
                                    {section.section.name}
                                  </div>
                                  <div className="section-type">
                                    {sectionTypeLabel}
                                  </div>
                                  <div className="section-cost">
                                    {totalCost.toLocaleString("ru-RU")} 〒
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );

                      return (
                        <>
                          {contractSections.length > 0 && (
                            <>
                              <h5 className="subsection-title">
                                Разделы договора
                              </h5>
                              {renderSections(
                                contractSections,
                                "Раздел договора",
                              )}
                            </>
                          )}

                          {constructionSections.length > 0 && (
                            <>
                              <h5 className="subsection-title">
                                Разделы рабочего проектирования
                              </h5>
                              {renderSections(
                                constructionSections,
                                "Рабочий проект",
                              )}
                            </>
                          )}
                        </>
                      );
                    })()}
                </div>

                {/* Настройка блоков и стоимости */}
                {Array.from(selectedSections).length > 0 && (
                  <div className="blocks-configuration-section">
                    <h4 className="subsection-title">
                      Настройка блоков и стоимости
                    </h4>
                    <p className="subsection-description">
                      Укажите, какие блоки и по какой цене будут выполнены в
                      рамках соглашения
                    </p>
                    {Array.from(selectedSections).map((sectionId) => {
                      const section = sectionsData[sectionId];
                      const selectedSectionMeta = findSectionById(sectionId);
                      const sectionName =
                        selectedSectionMeta?.section?.name ||
                        section?.section?.name ||
                        `Раздел #${sectionId}`;

                      const blocks = section.block_expenditures || [];
                      const sectionBlocks = selectedBlocks[sectionId] || {};
                      const sectionCosts = agreementCosts[sectionId] || {};
                      const hasDetailedBlocks = blocks.length > 0;

                      const getSelectedBlocksPlanTotal = () =>
                        Object.entries(sectionBlocks)
                          .filter(([, selected]) => selected)
                          .reduce((sum, [blockIndex]) => {
                            const block = blocks[Number(blockIndex)];
                            return sum + getAgreementBlockPlanCost(block);
                          }, 0);

                      const handleBlockToggle = (blockIndex) => {
                        const newSelectedBlocks = {
                          ...selectedBlocks,
                          [sectionId]: {
                            ...sectionBlocks,
                            [blockIndex]: !sectionBlocks[blockIndex],
                          },
                        };
                        setSelectedBlocks(newSelectedBlocks);

                        // Инициализируем стоимость по плану, если блок выбран
                        if (!sectionBlocks[blockIndex]) {
                          const block = blocks[blockIndex];
                          const planCost = getAgreementBlockPlanCost(block);
                          setAgreementCosts({
                            ...agreementCosts,
                            [sectionId]: {
                              ...sectionCosts,
                              [blockIndex]: planCost,
                            },
                          });
                        } else {
                          // Удаляем стоимость при снятии выбора
                          const newCosts = { ...sectionCosts };
                          delete newCosts[blockIndex];
                          setAgreementCosts({
                            ...agreementCosts,
                            [sectionId]: newCosts,
                          });
                        }
                      };

                      const handleCostChange = (blockIndex, value) => {
                        setAgreementCosts({
                          ...agreementCosts,
                          [sectionId]: {
                            ...sectionCosts,
                            [blockIndex]: parseFloat(value) || 0,
                          },
                        });
                      };

                      // Подсчет итогов для раздела
                      const planTotal = hasDetailedBlocks
                        ? getSelectedBlocksPlanTotal()
                        : getAgreementSectionPlanCost(
                            selectedSectionMeta,
                            blocks,
                          );

                      const agreementTotal = Object.values(sectionCosts).reduce(
                        (sum, cost) => sum + (cost || 0),
                        0,
                      );

                      return (
                        <div key={sectionId} className="section-blocks-config">
                          <div className="section-blocks-header">
                            <h5 className="section-blocks-title">
                              {sectionName}{" "}
                              {hasDetailedBlocks
                                ? "Рабочий проект"
                                : "Раздел договора"}
                            </h5>
                            <button
                              className="remove-section-btn"
                              onClick={() => {
                                const newSelected = new Set(selectedSections);
                                newSelected.delete(sectionId);
                                setSelectedSections(newSelected);
                                const newSelectedBlocks = { ...selectedBlocks };
                                delete newSelectedBlocks[sectionId];
                                setSelectedBlocks(newSelectedBlocks);
                                const newAgreementCosts = {
                                  ...agreementCosts,
                                };
                                delete newAgreementCosts[sectionId];
                                setAgreementCosts(newAgreementCosts);
                              }}
                            >
                              ×
                            </button>
                          </div>
                          {hasDetailedBlocks ? (
                            <>
                              <p className="section-blocks-subtitle">
                                Блоки раздела из финансового плана
                              </p>
                              <div className="blocks-table-container">
                                <table className="blocks-table">
                                  <thead>
                                    <tr>
                                      <th></th>
                                      {blocks.map((block, idx) => (
                                        <th key={idx}>
                                          {formatAgreementBlockLabel(block)}
                                        </th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr>
                                      <td>Выбрать в соглашение</td>
                                      {blocks.map((_, blockIndex) => {
                                        const isBlockSelected =
                                          sectionBlocks[blockIndex];
                                        return (
                                          <td key={blockIndex}>
                                            <input
                                              type="checkbox"
                                              checked={isBlockSelected || false}
                                              onChange={() =>
                                                handleBlockToggle(blockIndex)
                                              }
                                            />
                                          </td>
                                        );
                                      })}
                                    </tr>
                                    <tr>
                                      <td>Стоимость по плану</td>
                                      {blocks.map((_, blockIndex) => {
                                        const block = blocks[blockIndex];
                                        const planCost =
                                          getAgreementBlockPlanCost(block);
                                        return (
                                          <td key={blockIndex}>
                                            {planCost.toLocaleString("ru-RU")}{" "}
                                            〒
                                          </td>
                                        );
                                      })}
                                    </tr>
                                    <tr>
                                      <td>Стоимость по соглашению</td>
                                      {blocks.map((_, blockIndex) => {
                                        const isBlockSelected =
                                          sectionBlocks[blockIndex];
                                        const cost =
                                          sectionCosts[blockIndex] || 0;
                                        return (
                                          <td key={blockIndex}>
                                            {isBlockSelected ? (
                                              <div className="cost-input-wrapper">
                                                <input
                                                  type="number"
                                                  value={cost}
                                                  onChange={(e) =>
                                                    handleCostChange(
                                                      blockIndex,
                                                      e.target.value,
                                                    )
                                                  }
                                                  onFocus={
                                                    selectNumericInputValue
                                                  }
                                                  onClick={
                                                    selectNumericInputValue
                                                  }
                                                  className="cost-input"
                                                />
                                                <span className="cost-currency">
                                                  〒
                                                </span>
                                              </div>
                                            ) : (
                                              "—"
                                            )}
                                          </td>
                                        );
                                      })}
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </>
                          ) : (
                            <div className="blocks-table-container">
                              <table className="blocks-table">
                                <tbody>
                                  <tr>
                                    <td>Стоимость по плану</td>
                                    <td>
                                      {planTotal.toLocaleString("ru-RU")} 〒
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>Стоимость по соглашению</td>
                                    <td>
                                      <div className="cost-input-wrapper">
                                        <input
                                          type="number"
                                          value={
                                            sectionCosts.section_total || 0
                                          }
                                          onChange={(e) =>
                                            setAgreementCosts((prev) => ({
                                              ...prev,
                                              [sectionId]: {
                                                ...(prev[sectionId] || {}),
                                                section_total:
                                                  parseFloat(e.target.value) ||
                                                  0,
                                              },
                                            }))
                                          }
                                          onFocus={selectNumericInputValue}
                                          onClick={selectNumericInputValue}
                                          className="cost-input"
                                        />
                                        <span className="cost-currency">
                                          〒
                                        </span>
                                      </div>
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          )}
                          <div className="add-agreement-section-total">
                            <div className="add-agreement-section-total-item">
                              <span>По плану:</span>
                              <span>
                                {planTotal.toLocaleString("ru-RU")} 〒
                              </span>
                            </div>
                            <div className="add-agreement-section-total-item">
                              <span>По соглашению:</span>
                              <span>
                                {agreementTotal.toLocaleString("ru-RU")} 〒
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    {/* Общий итог */}
                    <div className="agreement-total">
                      <div className="agreement-total-label">
                        ИТОГО ПО СОГЛАШЕНИЮ
                      </div>
                      <div className="agreement-total-items">
                        <div className="agreement-total-item">
                          <span>По плану по выбранным разделам/блокам:</span>
                          <span>
                            {Array.from(selectedSections)
                              .reduce((sum, sectionId) => {
                                const section = sectionsData[sectionId];
                                const blocks =
                                  section?.block_expenditures || [];
                                const hasDetailedBlocks = blocks.length > 0;
                                if (!hasDetailedBlocks) {
                                  const selectedSectionMeta =
                                    findSectionById(sectionId);
                                  return (
                                    sum +
                                    getAgreementSectionPlanCost(
                                      selectedSectionMeta,
                                      blocks,
                                    )
                                  );
                                }
                                return (
                                  sum +
                                  Object.entries(
                                    selectedBlocks[sectionId] || {},
                                  )
                                    .filter(([, selected]) => selected)
                                    .reduce((sectionSum, [blockIndex]) => {
                                      const block =
                                        blocks[parseInt(blockIndex, 10)];
                                      return (
                                        sectionSum +
                                        getAgreementBlockPlanCost(block)
                                      );
                                    }, 0)
                                );
                              }, 0)
                              .toLocaleString("ru-RU")}{" "}
                            〒
                          </span>
                        </div>
                        <div className="agreement-total-item">
                          <span>По соглашению:</span>
                          <span>
                            {Array.from(selectedSections)
                              .reduce((sum, sectionId) => {
                                const sectionCosts =
                                  agreementCosts[sectionId] || {};
                                return (
                                  sum +
                                  Object.values(sectionCosts).reduce(
                                    (sectionSum, cost) =>
                                      sectionSum + (cost || 0),
                                    0,
                                  )
                                );
                              }, 0)
                              .toLocaleString("ru-RU")}{" "}
                            〒
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="add-agreement-actions">
                  <button
                    type="button"
                    className="btn-back"
                    onClick={() => handleStepClick(1)}
                  >
                    Назад
                  </button>
                  <button
                    type="button"
                    className="btn-next"
                    onClick={handleNext}
                    disabled={selectedSections.size === 0}
                  >
                    Далее
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {currentStep === 3 && (
          <div className="add-agreement-content add-agreement-step3">
            <div className="agreement-info-display agreement-info-display--step3">
              <span className="agreement-info-text">
                Соглашение {agreementNumber} от:{" "}
                {agreementDate?.toLocaleDateString("ru-RU", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })}
              </span>
            </div>

            <h3 className="section-title">Сроки и порядок оплаты</h3>

            {/* Сроки выполнения */}
            <div className="form-group">
              <h4 className="subsection-title">Сроки выполнения</h4>
              <p className="subsection-description">
                Выберите срок выполнения*
              </p>
              <div className="execution-dates-row">
                <div className="date-field-group">
                  <label className="date-field-label">с:</label>
                  <MaskedRuDatePicker
                    selected={executionStartDate}
                    onChange={setExecutionStartDate}
                    className="date-field-input"
                  />
                </div>
                <div className="date-field-group">
                  <label className="date-field-label">по:</label>
                  <MaskedRuDatePicker
                    selected={executionEndDate}
                    onChange={setExecutionEndDate}
                    className="date-field-input"
                    minDate={executionStartDate}
                  />
                </div>
              </div>
            </div>

            {/* Порядок оплаты */}
            <div className="form-group">
              <h4 className="subsection-title">Порядок оплаты</h4>
              <p className="subsection-description">
                Выберите шаблон графика оплат или задайте проценты вручную
              </p>
              <div className="form-group">
                <label className="form-label">Шаблон графика оплат</label>
                <Select
                  styles={selectStyles}
                  options={PAYMENT_TEMPLATES}
                  value={PAYMENT_TEMPLATES.find(
                    (t) => t.value === paymentTemplate,
                  )}
                  onChange={handlePaymentTemplateChange}
                  placeholder="Выберите шаблон"
                />
              </div>
              {paymentTemplate === "custom" && (
                <div className="custom-payment-percentages">
                  <p className="subsection-description">
                    Укажите проценты и описания по этапам. Сумма должна быть
                    100%. Нулевой этап не попадёт в график. Если описание не
                    заполнено, для базовых этапов используется стандартное.
                  </p>
                  <div className="custom-payment-percentages-grid">
                    {getCustomPaymentStageFields(
                      customPaymentPercentages,
                    ).map((field, index) => (
                      <div
                        key={field.key}
                        className="custom-payment-percent-field"
                      >
                        <div className="custom-payment-percent-label-row">
                          <label
                            className="form-label"
                            htmlFor={`custom-payment-${field.key}`}
                          >
                            {field.label}
                          </label>
                          {field.removable && (
                            <button
                              type="button"
                              className="remove-section-btn custom-payment-remove-stage-btn"
                              onClick={() =>
                                handleRemoveCustomPaymentStage(index)
                              }
                              title="Удалить этап"
                              aria-label={`Удалить этап «${field.label}»`}
                            >
                              ×
                            </button>
                          )}
                        </div>
                        <div className="custom-payment-percent-input-wrap">
                          <input
                            id={`custom-payment-${field.key}`}
                            type="text"
                            inputMode="numeric"
                            className="custom-payment-percent-input"
                            value={customPaymentPercentages[index] ?? 0}
                            onChange={(e) =>
                              handleCustomPercentChange(index, e.target.value)
                            }
                          />
                          <span className="custom-payment-percent-suffix">
                            %
                          </span>
                        </div>
                        <textarea
                          className="custom-payment-description-input"
                          rows={3}
                          value={customPaymentDescriptions[index] ?? ""}
                          onChange={(e) =>
                            handleCustomDescriptionChange(
                              index,
                              e.target.value,
                            )
                          }
                          placeholder={
                            getDefaultPaymentStageDescription(
                              index,
                              customPaymentPercentages,
                            ) || "Описание этапа (условие оплаты)"
                          }
                          aria-label={`Описание этапа «${field.label}»`}
                        />
                      </div>
                    ))}
                  </div>
                  {normalizePaymentPercentages(customPaymentPercentages)
                    .length < MAX_PAYMENT_STAGES_COUNT && (
                    <button
                      type="button"
                      className="btn-secondary custom-payment-add-stage-btn"
                      onClick={handleAddCustomPaymentStage}
                    >
                      + Добавить этап
                    </button>
                  )}
                  <div
                    className={`custom-payment-percentages-sum ${
                      isPaymentScheduleValid
                        ? "custom-payment-percentages-sum--ok"
                        : "custom-payment-percentages-sum--error"
                    }`}
                  >
                    Итого: {customPaymentPercentagesSum}%
                    {!isPaymentScheduleValid && " — сумма должна быть 100%"}
                  </div>
                </div>
              )}
            </div>

            {/* Этапы оплаты */}
            {selectedTemplateStages.length > 0 && (
              <div className="payment-stages-section">
                <h4 className="subsection-title">Этапы оплаты</h4>
                {selectedTemplateStages.map((stage) => {
                  const amount = Math.round(
                    (totalAgreementCost * stage.percent) / 100,
                  );
                  return (
                    <div key={stage.key} className="payment-stage-card">
                      <div className="payment-stage-header">
                        <span className="payment-stage-label">
                          {stage.label}: {stage.percent}%
                        </span>
                        <span className="payment-stage-amount">
                          {amount.toLocaleString("ru-RU")} 〒
                        </span>
                      </div>
                      {stage.description && (
                        <p className="payment-stage-description">
                          {stage.description}
                        </p>
                      )}
                      <p className="payment-stage-terms">{stage.terms}</p>
                    </div>
                  );
                })}
                <div className="payment-stage-total">
                  <span className="payment-stage-total-label">Итого:</span>
                  <span className="payment-stage-total-value">
                    {totalAgreementCost.toLocaleString("ru-RU")} 〒
                  </span>
                </div>
              </div>
            )}

            {/* Предпросмотр соглашения */}
            <div className="agreement-preview-section">
              <h4 className="subsection-title">Предпросмотр соглашения</h4>
              <div className="agreement-preview-card">
                <div className="agreement-preview-row">
                  <span className="agreement-preview-label">Соглашение:</span>
                  <span className="agreement-preview-value">
                    {agreementNumber}
                  </span>
                </div>
                <div className="agreement-preview-row">
                  <span className="agreement-preview-label">Дата:</span>
                  <span className="agreement-preview-value">
                    {agreementDate?.toLocaleDateString("ru-RU", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                    })}
                  </span>
                </div>
                <div className="agreement-preview-row">
                  <span className="agreement-preview-label">Стороны:</span>
                  <span className="agreement-preview-value">
                    {genproektDisplayName} и {getSubcontractorName()}{" "}
                    (Субподрядчик)
                  </span>
                </div>
                <div className="agreement-preview-row">
                  <span className="agreement-preview-label">Предмет:</span>
                  <span className="agreement-preview-value">
                    {getAgreementSubject()}
                  </span>
                </div>
                <div className="agreement-preview-row">
                  <span className="agreement-preview-label">Стоимость:</span>
                  <span className="agreement-preview-value">
                    {totalAgreementCost.toLocaleString("ru-RU")} 〒 (без НДС)
                  </span>
                </div>
                <div className="agreement-preview-row">
                  <span className="agreement-preview-label">
                    Срок выполнения:
                  </span>
                  <span className="agreement-preview-value">
                    {formatDateRange(executionStartDate, executionEndDate)}
                  </span>
                </div>
                <div className="agreement-preview-row">
                  <span className="agreement-preview-label">
                    Порядок оплаты:
                  </span>
                  <span className="agreement-preview-value">
                    {paymentTemplate === "custom"
                      ? `Свой график (${formatPaymentPercentagesLabel(
                          selectedPaymentPercentages,
                        )})`
                      : PAYMENT_TEMPLATES.find(
                          (t) => t.value === paymentTemplate,
                        )?.label || "—"}
                  </span>
                </div>
                {/* Таблица графика оплат */}
                {selectedTemplateStages.length > 0 && (
                  <div className="agreement-preview-table-wrap">
                    <table className="agreement-preview-table">
                      <thead>
                        <tr>
                          <th>Этап</th>
                          <th>%</th>
                          <th>План Дата</th>
                          <th>План Сумма</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedTemplateStages.map((stage, stageIndex) => {
                          const planDate = getPaymentPlanDateForStage(
                            selectedTemplateStages,
                            paymentPlanDates,
                            executionStartDate,
                            stageIndex,
                          );
                          const minPlanDate = getMinPaymentPlanDateForStage(
                            selectedTemplateStages,
                            paymentPlanDates,
                            executionStartDate,
                            stageIndex,
                          );
                          return (
                            <tr key={stage.key}>
                              <td>{stage.label}</td>
                              <td>{stage.percent}%</td>
                              <td className="agreement-preview-date-cell">
                                <MaskedRuDatePicker
                                  selected={planDate}
                                  onChange={(date) =>
                                    handlePaymentPlanDateChange(
                                      stage.key,
                                      stageIndex,
                                      date,
                                    )
                                  }
                                  minDate={minPlanDate ?? undefined}
                                  className="agreement-preview-date-input"
                                />
                              </td>
                              <td>
                                {Math.round(
                                  (totalAgreementCost * stage.percent) / 100,
                                ).toLocaleString("ru-RU")}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            {saveError && (
              <div className="add-agreement-submit-error add-agreement-submit-error--step3">
                {saveError}
              </div>
            )}
            {saveInfo && (
              <div className="add-agreement-submit-info add-agreement-submit-info--step3">
                {saveInfo}
              </div>
            )}
            <div className="add-agreement-actions add-agreement-actions--step3">
              <button
                type="button"
                className="btn-back"
                onClick={() => handleStepClick(2)}
              >
                Назад
              </button>
              <button
                type="button"
                className="btn-cancel-text"
                onClick={onClose}
              >
                Отмена
              </button>
              <div className="add-agreement-actions-right">
                {/* <button
                  type="button"
                  className="btn-primary btn-send"
                  disabled={!executionStartDate || !executionEndDate}
                >
                  Отправить на подпись
                </button> */}
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleSave}
                  disabled={
                    savingAgreement || !projectId || !isPaymentScheduleValid
                  }
                >
                  {savingAgreement ? "Сохранение…" : "Сохранить"}
                </button>
                {/* <button type="button" className="btn-secondary">
                  Печать PDF
                </button> */}
              </div>
            </div>
          </div>
        )}
      </div>

      <AddCounterpartyModal
        open={showAddSubcontractorModal}
        onClose={() => setShowAddSubcontractorModal(false)}
        defaultType="counterparty"
        onCounterpartyCreated={(newCounterparty) => {
          setExtraSubcontractors((prev) => [...prev, newCounterparty]);
          setSelectedPerformer({ _source: "counterparty", ...newCounterparty });
          setShowAddSubcontractorModal(false);
        }}
      />
    </div>
  );
};

export default AddAgreement;
